import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from './ToastSystem';

/**
 * DsSwitch — 全プラットフォーム共通のオン/オフ スイッチ（Web 正規実装）。
 *
 * - 楽観的更新: クリックで表示を即時切替。`onChange(next)` が Promise を返したら
 *   完了を待ち、失敗（reject/throw）時は表示を元に戻して通知する。
 * - 通知: `onError` 省略時は既存トースト（useToast）でエラー表示。
 * - 操作中(pending)は二重発火防止のため非インタラクティブ。
 * - 形崩れ対策はCSS側（.ds-switch / components.css）で寸法・aspect-ratio を固定。
 *
 * props:
 *   checked: boolean
 *   onChange: (next:boolean) => void | Promise<any>
 *   onError?: (err) => void
 *   disabled?: boolean
 *   label?: ReactNode         右側に表示するラベル（任意）
 *   className?: string        追加クラス（レイアウト用）
 *   id, name, ariaLabel/'aria-label'
 *   errorText?: string        既定トーストの文言
 */
export default function DsSwitch({
  checked,
  onChange,
  onError,
  disabled = false,
  label,
  className = '',
  id,
  name,
  ariaLabel,
  errorText = '変更を保存できませんでした',
  ...rest
}) {
  const toast = useToast();
  const [optimistic, setOptimistic] = useState(null); // null = props.checked に従う
  const [pending, setPending] = useState(false);
  const isChecked = optimistic !== null ? optimistic : Boolean(checked);

  // 外部 checked が確定したら（保存中でなければ）楽観表示を解除して同期
  useEffect(() => {
    if (!pending) setOptimistic(null);
  }, [checked, pending]);

  const notifyError = useCallback(
    (err) => {
      if (onError) onError(err);
      else toast.error(errorText);
    },
    [onError, toast, errorText],
  );

  const handleChange = useCallback(() => {
    if (disabled || pending) return;
    const prev = Boolean(checked);
    const next = !isChecked;
    setOptimistic(next); // 楽観反映（即時）

    let result;
    try {
      result = onChange?.(next);
    } catch (err) {
      setOptimistic(prev);
      notifyError(err);
      return;
    }
    if (result && typeof result.then === 'function') {
      setPending(true);
      Promise.resolve(result)
        .then(
          () => setOptimistic(null), // 成功: props.checked に追従
          (err) => {
            setOptimistic(prev); // 失敗: 元へ巻き戻し
            notifyError(err);
          },
        )
        .finally(() => setPending(false));
    }
    // 同期 onChange は props.checked 更新時に useEffect で解除される
  }, [checked, isChecked, disabled, pending, onChange, notifyError]);

  const ariaLabelFinal = ariaLabel || rest['aria-label'];
  delete rest['aria-label'];

  return (
    <label
      className={`ds-switch${className ? ` ${className}` : ''}`}
      data-pending={pending ? '' : undefined}
    >
      <input
        type="checkbox"
        role="switch"
        id={id}
        name={name}
        checked={isChecked}
        disabled={disabled || pending}
        onChange={handleChange}
        aria-label={ariaLabelFinal}
        aria-checked={isChecked}
        {...rest}
      />
      {label != null && <span className="ds-switch__label">{label}</span>}
    </label>
  );
}
